<template>
    <div class="standalone-well-video">
        <div
            ref="wellVideoContainer"
            class="well-video-container"
            :class="{ 'three-d': currentView3d, 'settings-open': showVideoSettings }"
        >
            <div class="well-view-top">
                <well-time-view :current-well-time_h="currentWellTime_h" />

                <pill-switch
                    v-if="!isFdaFriendly && segmentationsAreEnabled"
                    v-b-tooltip.hover
                    :title="$translate('show_graphical_segmentations')"
                    input-id="seek_mode_switch"
                    :is-checked="showAnnotationGraphics"
                    :label="$translate('overlay')"
                    @changed="toggleOverlay"
                />
            </div>

            <div
                class="well-video-wrapper"
                :class="{ 'overflow-hidden': zoom === 1 }"
                @click="togglePlayerButtons"
            >
                <b-spinner v-if="!videoPlaybackReady" class="video-spinner" variant="primary" />
                <video
                    v-for="(video, index) in videos"
                    :id="`video_for_well_${well._id}_for_focal_${video.focal}`"
                    :key="video.focal"
                    class="well-view-video"
                    preload="auto"
                    playsinline
                    :focal="video.focal"
                    :controls="false"
                    :style="
                        `z-index: ${focalsLength - index}; ` +
                        `-webkit-filter: brightness(${brightness}) invert(${
                            invert ? '1' : '0'
                        }); ` +
                        (currentView3d
                            ? `opacity: ${((index + 1) / focalsLength) * 0.2 + 0.1};`
                            : `visibility: ${video.focal === focal ? 'visible' : 'hidden'};`) +
                        ` transform: scale(${zoom}, ${zoom});` +
                        (currentView3d
                            ? ` left: ${horizontalTilt * (video.focal / focalsLength)}%; top: ${
                                  verticalTilt * (video.focal / focalsLength)
                              }%`
                            : '')
                    "
                    @mousemove="onMouseMove"
                >
                    <!-- timecode postfix is for thumbnail in iPhone & iPad -->
                    <source :src="`${video.videoUrl}#t=0.001`" type="video/mp4" />
                </video>

                <well-status-overlay
                    v-if="videoPlaybackReady"
                    class="rounded-top-lg"
                    :well="well"
                    :style="`z-index: ${focalsLength};  transform: scale(${zoom}, ${zoom});`"
                />

                <well-graphical-segmentations
                    v-if="segmentationsAreEnabled"
                    class="w-100 h-100 rounded-top-lg"
                    :style="`z-index: ${focalsLength + 1};  transform: scale(${zoom}, ${zoom});`"
                    :segmentations="segmentations"
                    :current-run-index="currentRunIndex"
                />

                <player-navigation
                    v-if="showPlayerButtons"
                    :play-state="playState"
                    video-seek-mode="event"
                    :style="`z-index: ${focalsLength + 2};`"
                    @playToggled="setPlayState"
                    @skipClicked="skipToEvent"
                    @click.native.stop="togglePlayerButtons"
                />

                <well-no-embryo-overlay
                    v-if="!well.runs"
                    class="w-100 h-100 rounded-top-lg"
                    :style="`z-index: ${focalsLength + 1};  transform: scale(${zoom}, ${zoom});`"
                />
            </div>

            <div class="video-bottom-controls">
                <img
                    v-b-tooltip.hover
                    class="three-d-button"
                    :title="$translate('3d_view')"
                    :src="`/static/svg/${currentView3d ? 'icon-3d-blue-box' : 'icon-3d-dark'}.svg`"
                    alt=""
                    @click="toggle3dMode"
                />
                <i class="fa fa-angle-down" @click.stop.prevent="toggleVideoSettings" />
            </div>
        </div>

        <well-graphical-events-bar
            v-if="!isFdaFriendly"
            :well="well"
            :slide-incubation-length="slideIncubationLength"
        />

        <standalone-video-seek-bar
            :well="well"
            :video-length_s="videoLength_s"
            :current-time-code_s="currentTimeCode_s"
            :first-run-time_h="firstRunTime_h"
            :current-well-time_h="currentWellTime_h"
            :slide-incubation-length="slideIncubationLength"
            @seekVideoTo="seekVideoTo"
        />

        <div class="bottom-panel">
            <standalone-video-controls
                :well="well"
                :invert="invert"
                :brightness="brightness"
                :disabled="!videoPlaybackReady"
                :play-state="playState"
                :selected-m-k-event="selectedMKEvent"
                :selected-istanbul-event="selectedIstanbulEvent"
                :selected-day="selectedDay"
                :skip-frames-amount="skipFramesAmount"
                :delay-between-cycles_m="delayBetweenCycles_m"
                :hours-since-fertilization="selectedTreatment.hoursSinceFertilization"
                @skipVideo="skipFrames"
                @skipFramesAmountChanged="setSkipFramesAmount"
                @skipToMkEvent="skipVideoToMkEvent"
                @skipToIstanbulEvent="skipVideoToIstanbulEvent"
                @skipToDay="skipVideoToDay"
                @playStateChanged="setPlayState"
            />
        </div>

        <video-settings v-if="showVideoSettings" />
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { ALL_DAYS, ALL_ISTANBUL_EVENTS, INSTRUMENT_TYPE } from '../../business/const'
import Segmentations from '../../../mock/data/Segmentations'
import Utils from '../../business/utils/Utils'
import UIUtils from '../../business/utils/UIUtils'
import EventDispatcher from '../../business/services/EventDispatcher'
import KeyboardService from '../../business/services/KeyboardService'

import PillSwitch from './common/PillSwitch'
import WellTimeView from './common/WellTimeView'
import WellGraphicalEventsBar from './common/WellGraphicalEventsBar'
import StandaloneVideoSeekBar from './common/StandaloneVideoSeekBar'
import StandaloneVideoControls from './common/StandaloneVideoControls'
import WellGraphicalSegmentations from './WellGraphicalSegmentations'
import WellNoEmbryoOverlay from './WellNoEmbryoOverlay'
import WellStatusOverlay from './common/WellStatusOverlay'
import PlayerNavigation from './video/PlayerNavigation'
import VideoSettings from './tablet/VideoSettings'

export default {
    name: 'StandaloneWellVideo',
    components: {
        WellStatusOverlay,
        WellNoEmbryoOverlay,
        PillSwitch,
        WellGraphicalSegmentations,
        StandaloneVideoControls,
        StandaloneVideoSeekBar,
        WellGraphicalEventsBar,
        WellTimeView,
        PlayerNavigation,
        VideoSettings
    },
    props: {
        well: {
            type: Object,
            required: true
        },
        isDemo: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            players: [],
            currentView3d: 0,
            horizontalTilt: 0,
            verticalTilt: 0,
            playState: 'pause',
            skipFramesAmount: 1,
            currentTimeCode_s: 0,
            showVideoSettings: false,
            showPlayerButtons: true,
            selectedIstanbulEvent: null,
            selectedDay: null
        }
    },
    computed: {
        ...mapState({
            selectedTreatment: state => state.timelapse.selectedTreatment,
            videoPlaybackReady: state => state.timelapse.videoPlaybackReady,
            showAnnotationGraphics: state => state.timelapse.showAnnotationGraphics
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            availableFocals: 'timelapse/availableFocals',
            getSelectedInstrument: 'timelapse/getSelectedInstrument'
        }),
        zoom() {
            return this.well.zoom
        },
        brightness() {
            return this.well.brightness
        },
        invert() {
            return this.well.invert
        },
        focal() {
            return this.well.focal
        },
        videos() {
            if (Utils.isMobileSize()) {
                return this.well.videos.high.filter(video => video.focal === 0)
            } else {
                return this.well.videos.high
            }
        },
        focalsLength() {
            return this.videos.length
        },
        fpsCalibrationTime_s() {
            if (!this.videoFPS) return 0

            return Math.round((1 / this.videoFPS / 2) * 1000000) / 1000000
        },
        slide() {
            if (!this.selectedTreatment) return null

            const { slides = [] } = this.selectedTreatment

            return slides.find(_slide => _slide._id === this.well.slideId)
        },
        videoLength_s() {
            return this.slide.videoLength
        },
        numberOfRuns() {
            return this.slide.numberOfRuns
        },
        videoFPS() {
            return Math.round((this.numberOfRuns + 1) / this.videoLength_s)
        },
        delayBetweenCycles_m() {
            return Math.round(this.slide.delayBetweenCycles)
        },
        firstRunTime_h() {
            return (
                (this.well.runs.filter(_run => !!_run)[0] &&
                    this.well.runs.filter(_run => !!_run)[0].time) ||
                0
            )
        },
        slideIncubationLength() {
            return (
                (this.well.runs.filter(_run => !!_run).last() &&
                    this.well.runs.filter(_run => !!_run).last().time) ||
                'na'
            )
        },
        segmentations() {
            if (this.isDemo) {
                return Segmentations
            }
            return this.well.inferenceData.segmentations
        },
        currentRunIndex() {
            return Utils.calculateRunIndex(
                this.currentTimeCode_s,
                this.numberOfRuns,
                this.videoLength_s
            )
        },
        currentRun() {
            return this.well.runs[this.currentRunIndex]
        },
        selectedMKEvent() {
            return this.currentRun ? this.currentRun.mkEvent : {}
        },
        currentWellTime_h() {
            return this.currentRun ? this.currentRun.time : 0
        },
        segmentationsAreEnabled() {
            const { GERI, MIRI } = INSTRUMENT_TYPE
            const { type } = this.getSelectedInstrument

            const isGeriOrMiri = [GERI, MIRI].includes(type)

            return !isGeriOrMiri && this.videoPlaybackReady && this.focal === 0
        },
        wells() {
            return this.selectedTreatment ? this.selectedTreatment.wellsByIndex : []
        },
        wellIndex() {
            return this.wells.findIndex(well => well._id === this.well._id)
        },
        isFirstWell() {
            return this.wellIndex === 0
        },
        isLastWell() {
            return this.wellIndex === this.wells.length - 1
        }
    },
    watch: {
        videoPlaybackReady(isReady) {
            if (isReady) {
                this.players[0].addEventListener('timeupdate', this.onTimeUpdate)
                this.subscribeToKeys()
                this.$store.commit('video/standaloneVideoCurrentTime_H', this.currentWellTime_h)
                this.syncEvents()

                UIUtils.hideLoadingMask()
            } else {
                UIUtils.showLoadingMask()
            }
        },
        playState(newState) {
            this.$log.videoEvent('play_stop')
            newState === 'play' ? this.playVideo() : this.stopVideo()
        },
        selectedMKEvent(val) {
            this.$emit('currentEventChanged', val)
        },
        currentRun() {
            this.syncEvents()
        },
        currentWellTime_h() {
            this.$store.commit('video/standaloneVideoCurrentTime_H', this.currentWellTime_h)
        }
    },
    mounted() {
        this.loadVideos()
        EventDispatcher.$on('SKIP_VIDEO_TO_RUN', this.skipVideoToFrame)
    },
    beforeDestroy() {
        this.unsubscribeFromKeys()
        EventDispatcher.$off('SKIP_VIDEO_TO_RUN', this.skipVideoToFrame)
    },
    methods: {
        loadVideos() {
            const videoLoadStartTime = new Date().getTime()
            this.$store.commit('timelapse/videoPlaybackReady', false)

            this.videos.forEach(video => {
                const videoElId = `video_for_well_${this.well._id}_for_focal_${video.focal}`
                const player = document.getElementById(videoElId)

                if (player) {
                    this.players.push(player)
                }
            })

            let loadCount = 0
            const self = this

            this.players.forEach(player => {
                player.addEventListener('canplaythrough', function () {
                    // only when last video finished loading
                    if (++loadCount === self.focalsLength && !self.videoPlaybackReady) {
                        self.checkDuration()

                        self.$store.commit('timelapse/videoPlaybackReady', true)

                        const videoLoadFinishTime = new Date().getTime()
                        const loadingTimeSec = (videoLoadFinishTime - videoLoadStartTime) / 1000
                        self.$log.videoEvent('multi_focal_video_loaded', {
                            loadingTimeSec
                        })
                    }
                })
            })
            // when video ended change state to pause
            if (this.players.length > 0) {
                this.players[0].addEventListener('ended', function () {
                    self.setPlayState('pause', 'videoEnded')
                })
            }
        },
        checkDuration() {
            const areEqual = this.players
                .map(player => player.duration)
                .every((val, i, arr) => val === arr[0])

            if (!areEqual) {
                UIUtils.showModal('error', $data => {
                    $data.errorMessage = 'corrupted_video'
                })
            }
        },
        subscribeToKeys() {
            KeyboardService.subscribeTo(
                'focusedWell',
                'Space',
                () => {
                    if (this.videoPlaybackReady) {
                        this.setPlayState(this.playState === 'play' ? 'pause' : 'play', 'spaceKey')
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'ArrowLeft',
                () => {
                    if (this.videoPlaybackReady) {
                        this.skipFrames(false)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'ArrowRight',
                () => {
                    if (this.videoPlaybackReady) {
                        this.skipFrames(true)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'KeyA',
                () => {
                    console.log(
                        'skip back, KeyA, videoPlaybackReady:',
                        this.videoPlaybackReady,
                        '(should be true)'
                    )
                    if (this.videoPlaybackReady) {
                        this.skipFrames(false)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'KeyD',
                () => {
                    console.log(
                        'skip fwd, KeyD, videoPlaybackReady:',
                        this.videoPlaybackReady,
                        '(should be true)'
                    )
                    if (this.videoPlaybackReady) {
                        this.skipFrames(true)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'KeyW',
                () => {
                    console.log(
                        'focal up, KeyW, videoPlaybackReady:',
                        this.videoPlaybackReady,
                        '(should be true)'
                    )
                    if (this.videoPlaybackReady) {
                        this.setFocal(true)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'KeyS',
                () => {
                    console.log(
                        'focal down, KeyS, videoPlaybackReady:',
                        this.videoPlaybackReady,
                        '(should be true)'
                    )
                    if (this.videoPlaybackReady) {
                        this.setFocal(false)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'KeyE',
                () => {
                    console.log('next well, KeyE')
                    if (!this.isLastWell) {
                        this.selectWell(true)
                    }
                },
                true
            )
            KeyboardService.subscribeTo(
                'focusedWell',
                'KeyQ',
                () => {
                    console.log('previous well, KeyQ')
                    if (!this.isFirstWell) {
                        this.selectWell(false)
                    }
                },
                true
            )
        },
        unsubscribeFromKeys() {
            KeyboardService.unsubscribeAll('focusedWell', 'Space')
            KeyboardService.unsubscribeAll('focusedWell', 'ArrowLeft')
            KeyboardService.unsubscribeAll('focusedWell', 'ArrowRight')
            KeyboardService.unsubscribeAll('focusedWell', 'KeyA')
            KeyboardService.unsubscribeAll('focusedWell', 'KeyD')
            KeyboardService.unsubscribeAll('focusedWell', 'KeyW')
            KeyboardService.unsubscribeAll('focusedWell', 'KeyS')
            KeyboardService.unsubscribeAll('focusedWell', 'KeyE')
            KeyboardService.unsubscribeAll('focusedWell', 'KeyQ')
        },
        setPlayState(playState, method = 'playButton') {
            this.$log.videoEvent('toggle_play_well_page', {
                newPlayState: playState,
                focal: this.focal,
                method
            })

            if (this.players.length) {
                this.playState = playState
            }
        },
        playVideo() {
            this.players.forEach(player => player.play())
        },
        stopVideo() {
            this.players.forEach(player => player.pause())
        },
        setSkipFramesAmount(amount) {
            this.$log.videoEvent('skip_frames_amount_changed', amount)

            this.skipFramesAmount = amount
        },
        onTimeUpdate() {
            const currentTime = this.players[0].currentTime

            let timecode = parseFloat(currentTime.toFixed(2))

            if (timecode > 0) {
                timecode -= this.fpsCalibrationTime_s
            }

            this.currentTimeCode_s = timecode
        },
        seekVideoTo(timecode) {
            if (this.players.length && this.videoPlaybackReady) {
                const seekStartTime = new Date().getTime()

                timecode = parseFloat(timecode.toFixed(2))

                let additionalTimeFraction =
                    (Math.round(timecode * 1000000) % Math.round((1 / this.videoFPS) * 1000000)) /
                    1000000

                additionalTimeFraction = parseFloat(additionalTimeFraction.toFixed(4))

                if (additionalTimeFraction > 0) {
                    timecode = timecode - additionalTimeFraction
                }

                timecode += this.fpsCalibrationTime_s
                timecode = parseFloat(timecode.toFixed(2))
                this.players.forEach(player => (player.currentTime = timecode))

                const seekFinishTime = new Date().getTime()
                const seekTimeSec = (seekFinishTime - seekStartTime) / 1000

                this.$log.videoEvent('seek_multi_focal_video', {
                    toTimeCode: timecode,
                    focal: this.focal,
                    playState: this.playState,
                    seekTimeSec
                })
            }
        },
        skipFrames(forward) {
            this.$log.videoEvent('skip_forward_backward', this.skipFramesAmount)

            const amount = forward ? this.skipFramesAmount : -this.skipFramesAmount

            this.skipVideoToFrame(amount, true)
        },
        skipToEvent(isForward) {
            const targetEvent = Utils.getNextPreviousItem(
                isForward,
                this.well.inferenceData.morphokinetics,
                this.selectedMKEvent
            )
            this.skipVideoToMkEvent(targetEvent)
        },
        skipVideoToMkEvent(targetMkEvent) {
            const runs = this.well.runs

            if (this.players.length > 0) {
                this.$log.videoEvent('skip_to_event', targetMkEvent.systemName)

                let nextMkEventRunIndex = null

                for (let i = 0; i < runs.length; i++) {
                    const run = runs[i]

                    if (run.mkEvent && run.mkEvent.value === targetMkEvent.value) {
                        nextMkEventRunIndex = i
                        break
                    }
                }

                if (nextMkEventRunIndex) {
                    this.skipVideoToFrame(nextMkEventRunIndex, false)
                }
            }
        },
        skipVideoToIstanbulEvent(istanbulEvent) {
            this.selectedIstanbulEvent = istanbulEvent

            const { runs = [] } = this.well
            const closestRunIndex = runs.findLastIndex(run => run.time <= istanbulEvent.hours)
            if (closestRunIndex > -1) {
                this.skipVideoToFrame(closestRunIndex)
            }
        },
        skipVideoToDay(day) {
            this.selectedDay = day

            const { runIndex } = Utils.getDayWithRun(day.label, this.well)
            if (runIndex >= 0) {
                this.skipVideoToFrame(runIndex)
            }
        },
        skipVideoToFrame(frame, add = false) {
            if (this.players.length > 0) {
                if (add) {
                    let timecode = this.players[0].currentTime

                    timecode = parseFloat(timecode.toFixed(2))

                    if (timecode > 0) {
                        timecode -= this.fpsCalibrationTime_s
                    }

                    this.seekVideoTo(timecode + frame / this.videoFPS)
                } else {
                    this.seekVideoTo(frame / this.videoFPS)
                }
            }
        },
        toggle3dMode() {
            this.currentView3d = !this.currentView3d

            this.$log.videoEvent('3d_toggled', {
                isActivated: this.currentView3d,
                focal: this.focal,
                playState: this.playState
            })
        },
        onMouseMove(event) {
            if (this.currentView3d) {
                const rect = this.$refs.wellVideoContainer.getBoundingClientRect()

                const left = event.clientX - rect.left
                const top = event.clientY - rect.top

                this.horizontalTilt = (left - (rect.right - rect.left) / 2) / 800
                this.verticalTilt = (top - (rect.bottom - rect.top) / 2) / 800
            }
        },
        toggleOverlay(isChecked) {
            this.$store.commit('timelapse/showAnnotationGraphics', isChecked)
        },
        toggleVideoSettings() {
            this.showVideoSettings = !this.showVideoSettings
        },
        togglePlayerButtons() {
            this.showPlayerButtons = !this.showPlayerButtons
        },
        syncSelectedIstanbulEvent() {
            if (this.currentRun && this.currentRun.time) {
                this.selectedIstanbulEvent = ALL_ISTANBUL_EVENTS.find(
                    x => x.hours > this.currentRun.time
                )
            }
        },
        syncSelectedDay() {
            if (!this.currentRun) return

            const currentDay = ALL_DAYS.findLast(day => day.hours < this.currentRun.time)
            if (currentDay) {
                this.selectedDay = currentDay
            }
        },
        syncEvents() {
            this.syncSelectedIstanbulEvent()
            this.syncSelectedDay()
        },
        selectWell(next) {
            this.$log.actionEvent('prev_next_well')

            const desiredIndex = this.wellIndex + (next ? 1 : -1)

            this.$mainController.selectWell(this.wells[desiredIndex])
        },
        setFocal(isHigher) {
            const delta = isHigher ? 1 : -1
            const currentFocalIndex = this.availableFocals.findIndex(focal => focal === this.focal)
            const desiredFocalIndex = currentFocalIndex + delta

            if (desiredFocalIndex >= 0 && desiredFocalIndex < this.availableFocals.length) {
                this.$log.videoEvent('focal_changed', {
                    fromFocal: this.focal,
                    toFocal: this.availableFocals[desiredFocalIndex],
                    playState: this.playState,
                    method: 'keyboard'
                })

                this.$store.commit('timelapse/wellFocal', this.availableFocals[desiredFocalIndex])
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.standalone-well-video {
    position: relative;
    background-color: $dark-3;

    .well-video-container {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        width: $video-width-height;
        height: $video-width-height;
        position: relative;

        &.three-d {
            background-color: black;
        }

        &.settings-open {
            .fa-angle-down {
                transform: rotate(180deg);
            }
        }

        .well-view-top {
            display: flex;
            position: absolute;
            right: 0;
            width: 100%;
            z-index: 12;

            .well-time-view {
                position: absolute;
                left: 1rem;
                margin-top: 1rem;
                margin-right: 0.25rem;
            }
            .pill-switch {
                position: absolute;
                right: 1rem;
                margin-top: 1rem;
                color: $light-3;
            }
        }

        .well-video-wrapper {
            overflow: auto;
            width: $video-width-height;
            height: $video-width-height;
            position: relative;

            .well-view-video {
                width: $video-width-height;
                height: $video-width-height;
                position: absolute;
                top: 0;
                left: 0;
                transform-origin: 0 0;

                @media screen and #{$new-break3open} {
                    border: 0.1rem solid $dark;
                    border-radius: $border-radius-lg;
                }
            }

            .player-navigation {
                @media screen and #{$new-break2close} {
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    background-color: $dark-3-alpha;
                }
                @media screen and #{$new-break3open} {
                    display: none;
                }
            }
        }

        .video-bottom-controls {
            width: 100%;
            position: absolute;
            bottom: 0;
            z-index: 20;

            .three-d-button {
                @media screen and #{$new-break2close} {
                    display: none;
                }
                @media screen and #{$new-break3open} {
                    position: absolute;
                    bottom: 1.4rem;
                    left: 1.5rem;
                    height: 1.5rem;
                    cursor: pointer;
                }
            }
            i.fa-angle-down {
                @media screen and #{$new-break2close} {
                    position: absolute;
                    bottom: 1.4rem;
                    right: 1.5rem;
                    color: $light-3;
                    font-size: 26px;
                }
                @media screen and #{$new-break3open} {
                    display: none;
                }
            }
        }
    }

    .well-graphical-events-bar {
        height: 0.5rem;
    }

    .bottom-panel {
        position: relative;
        width: $video-width-height;
        display: flex;
        justify-content: space-between;

        @media screen and #{$new-break3open} {
            margin-top: 0.5rem;
        }
    }

    .video-settings {
        @media screen and #{$new-break2close} {
            width: 100%;
            height: 290px;
            position: absolute;
            bottom: -275px;
            z-index: 20;
            box-shadow: 0 0 1rem $shadow;
        }
        @media screen and #{$new-break3open} {
            display: none;
        }
    }

    @media screen and #{$new-break2close} {
        width: $video-width-height;
        padding: 0;

        .well-graphical-events-bar,
        .standalone-video-controls {
            display: none;
        }
    }
    @media screen and #{$new-break3open} {
        width: calc(#{$video-width-height} + 1rem);

        @media screen and #{$new-break3}, #{$new-break4} {
            padding: 0 0.5rem;
        }
        @media screen and #{$new-break5open} {
            padding: 1rem 0.5rem;
        }
    }
}
</style>

<style lang="scss">
.standalone-well-video {
    .well-time-view {
        font-weight: 600;
        background-color: $dark-4;

        @media screen and #{$new-break2close} {
            padding: 0.25rem 0.5rem;
            font-size: 14px;

            .fa-clock-o {
                display: none;
            }
        }
        @media screen and #{$new-break3open} {
            padding: 0.5rem 1rem;
            font-size: 12pt;
        }
    }

    .player-navigation {
        @media screen and #{$new-break2close} {
            .backward,
            .forward,
            .play {
                i {
                    font-size: 40px;
                }
            }
        }
    }

    .well-no-embryo-overlay {
        span {
            font-size: 20pt !important;
        }
    }

    .standalone-video-seek-bar {
        @media screen and #{$new-break2close} {
            margin: 0 0.5rem;
            width: calc(100% - 1rem);
            position: absolute;
            bottom: -10px;

            .video-seek-bar {
                .slider {
                    .slider-track {
                        height: 5px;
                        background-color: $dark-2;
                        border-radius: 3px;
                    }

                    .slider-tick-container {
                        .slider-tick {
                            height: 5px;
                        }
                    }

                    .slider-handle {
                        width: 1rem;
                        height: 1rem;
                        margin-left: -0.5rem;
                        margin-top: 0;
                        border: none;
                    }
                }
            }
        }

        @media screen and #{$new-break3}, #{$new-break4} {
            width: 50vw;
            position: fixed;
            bottom: 0;
            right: 0;
            margin-bottom: 0 !important;
            background-color: $dark-5 !important;
            height: $well-video-controls-bottom-panel-height;
            padding: 1.7rem 1.7rem 1.7rem 2.7rem;

            .slider-track {
                background-color: $dark-3 !important;
                border-radius: 5rem !important;
            }
        }
    }

    .video-spinner {
        min-height: 2rem;
        position: absolute;
        z-index: 999;
        top: calc(50% - 1rem);
        left: calc(50% - 1rem);
    }
}
</style>
